/**
 * Subscription Manager
 * Handles plan selector UI (SubscriptionPlanSelector web component)
 * and cart API calls (SubscriptionCartManager).
 *
 * Requires: cart-api.js loaded first (provides window.addToCart).
 * Activated by: data-requires-subscription="true" on <product-form>.
 */

/* ==========================================================================
   SubscriptionPlanSelector — Web Component
   ========================================================================== */

class SubscriptionPlanSelector extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', this._onRadioChange.bind(this));
    });
  }

  _onRadioChange(evt) {
    const radio = evt.target;

    // Update card selected state
    this.querySelectorAll('.subscription-plan-selector__card').forEach((card) => {
      card.classList.toggle(
        'is-selected',
        card.querySelector('[type="radio"]') === radio
      );
    });

    const context = this.dataset.context;
    const lineKey = this.dataset.lineKey;
    const planId = radio.dataset.planId;
    const variantId = radio.dataset.variantId;

    // Dispatch event (for PDP: product-form can listen if needed)
    this.dispatchEvent(
      new CustomEvent('subscription:plan-changed', {
        bubbles: true,
        detail: { planId, variantId },
      })
    );

    // Cart contexts: change plan immediately on selection
    if ((context === 'cart-drawer' || context === 'cart') && lineKey) {
      this.classList.add('is-loading');
      SubscriptionCartManager.changePlan(lineKey, planId, variantId, context)
        .catch((err) => console.error('Plan change failed:', err))
        .finally(() => this.classList.remove('is-loading'));
    }
  }

  /** Returns { planId, variantId } of the currently checked radio, or null. */
  get selectedPlan() {
    const radio = this.querySelector('[type="radio"]:checked');
    if (!radio) return null;
    return { planId: radio.dataset.planId, variantId: radio.dataset.variantId };
  }
}

if (!customElements.get('subscription-plan-selector')) {
  customElements.define('subscription-plan-selector', SubscriptionPlanSelector);
}

/* ==========================================================================
   SubscriptionCartManager — singleton
   ========================================================================== */

// Serializes addBundle calls so the cart-state check below cannot race
// with itself across concurrent clicks (each click would otherwise read
// a stale "no subscription" state and add a duplicate sub line).
let _bundlePending = null;

const SubscriptionCartManager = {
  /**
   * Add a physical product to cart, plus the subscription line — but only if
   * the cart does not already contain any line with a selling_plan_allocation.
   * If a subscription is already in the cart, only the physical product is added;
   * additional subscriptions must be purchased explicitly by the customer.
   */
  async addBundle(productVariantId, subVariantId, subPlanId) {
    if (_bundlePending) {
      await _bundlePending.catch(() => {});
    }

    const promise = (async () => {
      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) {
        throw new Error(`Cart fetch error: ${cartRes.status}`);
      }
      const cart = await cartRes.json();

      const hasSubscription = (cart.items || []).some(
        (item) => item.selling_plan_allocation
      );

      const items = [{ id: parseInt(productVariantId, 10), quantity: 1 }];
      if (!hasSubscription) {
        items.push({
          id: parseInt(subVariantId, 10),
          quantity: 1,
          selling_plan: parseInt(subPlanId, 10),
        });
      }

      return window.addToCart(items);
    })();

    _bundlePending = promise;
    try {
      return await promise;
    } finally {
      if (_bundlePending === promise) _bundlePending = null;
    }
  },

  /**
   * Change the subscription plan of an existing line item in cart.
   * Shopify's /cart/change.js cannot change variant_id, so we remove the old
   * line and re-add with the new variant + selling plan in two steps.
   * @param {string} lineKey     - cart line item key (item.key)
   * @param {string} newPlanId   - new selling plan ID
   * @param {string} newVariantId - new subscription variant ID
   * @param {string} context     - 'cart-drawer' or 'cart'
   */
  async changePlan(lineKey, newPlanId, newVariantId, context) {
    const sectionsParam = context === 'cart-drawer' ? 'cart-drawer' : 'main-cart-items';

    // Step 1: Remove old subscription line
    const removeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });

    if (!removeResponse.ok) {
      const errData = await removeResponse.json().catch(() => ({}));
      throw new Error(errData.description || `Cart remove error: ${removeResponse.status}`);
    }

    // Step 2: Add new subscription line with correct variant + selling plan
    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(newVariantId, 10), quantity: 1, selling_plan: parseInt(newPlanId, 10) }],
        sections: sectionsParam,
        sections_url: window.location.pathname,
      }),
    });

    if (!addResponse.ok) {
      const errData = await addResponse.json().catch(() => ({}));
      throw new Error(errData.description || `Cart add error: ${addResponse.status}`);
    }

    const data = await addResponse.json();

    if (context === 'cart-drawer') {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        await customElements.whenDefined('cart-drawer');
        cartDrawer.renderContents(data);
      }
    } else {
      // Cart page: replace .js-contents inside items and footer sections
      const sectionHtml = data.sections?.['main-cart-items'];
      if (!sectionHtml) return;

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');

      const itemsTarget = document.querySelector('#main-cart-items .js-contents');
      const itemsSource = parsed.querySelector('#main-cart-items .js-contents');
      if (itemsTarget && itemsSource) itemsTarget.replaceWith(itemsSource);

      const footerTarget = document.querySelector('#main-cart-footer .js-contents');
      const footerSource = parsed.querySelector('#main-cart-footer .js-contents');
      if (footerTarget && footerSource) footerTarget.replaceWith(footerSource);

      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          if (typeof window.updateCartCount === 'function') {
            window.updateCartCount(cart.item_count);
          }
        });
    }
  },
};

window.SubscriptionCartManager = SubscriptionCartManager;

/* ==========================================================================
   Add-to-Cart intercept for requires-subscription products
   Fires in capture phase — before product-form.js handles submit.
   Activated when <product-form data-requires-subscription="true"> is present.
   ========================================================================== */

document.addEventListener(
  'submit',
  (evt) => {
    const form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest('product-form');
    if (!productForm || !productForm.dataset.requiresSubscription) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    const btn = productForm.querySelector('[type="submit"]');
    if (!btn || btn.disabled) return;

    const productVariantId = form.querySelector('[name="id"]')?.value;

    // PDP: read from subscription-plan-selector web component
    // Look inside productForm first, then in the closest product component (kit layout has selector outside form)
    const selector = productForm.querySelector('subscription-plan-selector')
      || productForm.closest('product-hero-kit, product-hero')?.querySelector('subscription-plan-selector');
    let subVariantId, subPlanId;

    if (selector) {
      const plan = selector.selectedPlan;
      subVariantId = plan?.variantId;
      subPlanId = plan?.planId;
    } else {
      // Shop/collection card: read default plan from button data attributes
      subVariantId = btn.dataset.defaultSubVariantId;
      subPlanId = btn.dataset.defaultSubPlanId;
    }

    if (!productVariantId || !subVariantId || !subPlanId) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = window.variantStrings?.adding || 'Adding...';

    SubscriptionCartManager.addBundle(productVariantId, subVariantId, subPlanId)
      .then(() => {
        btn.textContent = window.variantStrings?.added || 'Added';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      })
      .catch((err) => {
        console.error('Subscription bundle add error:', err);
        btn.textContent = originalText;
        btn.disabled = false;
      });
  },
  true // capture phase — fires before product-form.js
);
